import filter from "lodash/filter";
import head from "lodash/head";
import { split } from "sentence-splitter";
import { Store } from "redux";

import { mapVoices } from "./VoicesMap";
import { audioBoundaryReached } from "../../store/modules/audio/actions";
import { IAudioVoice } from "../../store/modules/audio/IAudioVoice";
import { rateToValue } from "../../utils/converSpeed";

//TODO: create observable instead of singleton. Follow this guide
//https://chrisnoring.gitbooks.io/rxjs-5-ultimate/content/observable-wrapping.html
class WebSpeechAPI {
  public defaultBroswerVoices: Array<SpeechSynthesisVoice> = [];
  public speechEnded: boolean;
  public isForcedCancel: boolean;
  public initialText: string;
  public synthVoices: Array<SpeechSynthesisVoice>;
  public wordIndex: number;
  public charIndex: number;

  public partWordIndex: number;
  public speechSynthesisUtterance: SpeechSynthesisUtterance;
  public sentenceIndex: number;
  public rawSentences: Array<any>;

  protected storeInstance: Store;
  protected _utteranceReadingInterval: number | undefined;
  protected _sentencesLeftFromUtteranceArray: Array<SpeechSynthesisUtterance>;
  protected currentSentenceCharPosition: number;
  protected  shift: number;

  private leftBoundaryPosition: number;
  private rightBoundaryPosition: number;

  private readonly OPERATION_INTERVAL = 200;

  constructor({ store } : { store: Store }) {

    speechSynthesis.onvoiceschanged = () => {
      this.defaultBroswerVoices = speechSynthesis.getVoices();
      this.speechEnded = true;
    };

    this.isForcedCancel = false;
    this.initialText = '';
    this.synthVoices = [];
    this.wordIndex = 0;
    this.charIndex = 0;
    this.speechEnded = true;
    this.partWordIndex = 1;
    this._utteranceReadingInterval = undefined;
    this._sentencesLeftFromUtteranceArray = [];
    this.speechSynthesisUtterance = new SpeechSynthesisUtterance();
    this.sentenceIndex = 0;
    this.rawSentences = [];
    this.leftBoundaryPosition = this.rightBoundaryPosition = 0;
    this.storeInstance = store;
    this.shift = 0;
    this.currentSentenceCharPosition = 0;
  }

  getText() {
    return this.speechSynthesisUtterance.text;
  }

  // eslint-disable-next-line class-methods-use-this
  getVoices(): Promise<Array<IAudioVoice>> {
    return new Promise((resolve) => {
      return resolve(this.filterVoices(speechSynthesis.getVoices()));
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async pause() {
    clearInterval(this._utteranceReadingInterval);
    await speechSynthesis.pause();
  }


  // eslint-disable-next-line class-methods-use-this
  async resume() {
    await speechSynthesis.resume();
  }

  // eslint-disable-next-line class-methods-use-this
  cancel(isForcedCancel = false) {
    if (isForcedCancel) {
      this.setText(this.initialText);
    }
    this.isForcedCancel = isForcedCancel;
    speechSynthesis.cancel();
    return new Promise((resolve) => {
      setTimeout(() => {
        return resolve();
      }, this.OPERATION_INTERVAL);
    });
  }

  /**
   * Workaround to fix google voices issue for
   * long phrases.
   */
  createUtteranceReadingInterval() {
    this._utteranceReadingInterval = window.setInterval(() => {
      speechSynthesis.pause();
      speechSynthesis.resume();
    }, 14000);

  }

  skipForward(shift?: number) {

    return this.performShift(shift || this.calculateShift());
  }

  skipBackward(shift?: number) {
    return this.performShiftBackward(shift || this.calculateShift());
  }

  /**
   * Calculate number of words to be shifted according to selected rate of speech
   * @returns {number}
   */
  calculateShift(): number {
    return Math.floor(rateToValue(this.speechSynthesisUtterance.rate) / 2);
  }


  /**
   * Calculate number of symbols in the sentence to skip after shift
   * @param wordsLeft
   * @param {number} wordsLeftLength
   * @param {number} sentencesToSkip
   * @param {boolean} isForward
   * @param {number} utteranceLengthDifference
   * @returns {number}
   */
  private calculateSymbolsToShift(wordsLeft: any, wordsLeftLength: number, sentencesToSkip: number, isForward = true, utteranceLengthDifference = 0): number {
    let symbolsCounter = 0;

    //Calculate starting position to speak from after shift
    for (let i = 0; i < wordsLeftLength; i++) {
      if(!wordsLeft[i]) {
        return 0;
      }

      symbolsCounter += wordsLeft[i].length + 1;
    }

    sentencesToSkip += utteranceLengthDifference;

    this._sentencesLeftFromUtteranceArray[sentencesToSkip].text =
      this._sentencesLeftFromUtteranceArray[sentencesToSkip].text.slice(symbolsCounter);

    if(isForward) {
      //Remove sentences that should be skipped from the queue
      for (let j = 0; j < sentencesToSkip; j++) {
        this._sentencesLeftFromUtteranceArray.shift();
      }
    }
    return symbolsCounter;
  }



  /**
   * Skip reading to word number passed in arguments
   * TODO: use this solution for skip forward and backward functionality. This should help minimize possible bugs.
   * @param {number} desiredPosition
   * @returns {any}
   */
  jumpToWord(desiredPosition: number) {

    return new Promise((resolve) => {

      return this.cancel(true).then(() => {
        //Address an issue with WEPSpeech API cancel method.

          let sentenceIndex = 0;
          let calculatedSymbolsNumber = 0;

          //Ensure that cached utterance queue is refreshed.
          this.setText(this.initialText);

          //Find sentence to skip to
          while(desiredPosition >= calculatedSymbolsNumber) {
            //Consider punctuation marks when calculated symbols to skip.
            calculatedSymbolsNumber += this._sentencesLeftFromUtteranceArray[sentenceIndex].text.length + this.rawSentences[this.sentenceIndex].punctuationMarks;
            sentenceIndex++;
          }

          //Remove utterances before the sentence to skip to
          for(let i = 0; i < sentenceIndex-1; i++) {
            this._sentencesLeftFromUtteranceArray.shift();
          }

          //Calculate symbol position inside the sentence
          calculatedSymbolsNumber -= this._sentencesLeftFromUtteranceArray[0].text.length;
          calculatedSymbolsNumber = desiredPosition - calculatedSymbolsNumber;

          //Ensure that speaking will not start from the middle of the word
          while (this._sentencesLeftFromUtteranceArray[0].text[calculatedSymbolsNumber] !== ' ' && calculatedSymbolsNumber > 0) {
            calculatedSymbolsNumber--;
          }

          //Cut starting sentence until speaking position.
          this._sentencesLeftFromUtteranceArray[0].text = this._sentencesLeftFromUtteranceArray[0].text.slice(calculatedSymbolsNumber);

          //Update positioning for highlight.
            this.shift = calculatedSymbolsNumber;
            this.sentenceIndex = sentenceIndex-1;
            return resolve();
      });
    })
  }


  /**
   * Skip backwards number of words passed in args
   * @param {number} desiredPosition
   * @returns {any}
   */
  performShiftBackward(desiredPosition: number) {
    let sentencesToSkip = 0;
    let wordsLeftLength = 0;
    let additionalSentencesNumber = this.sentenceIndex;

    //Skip backwards sentences which do not have sufficient number of words; Save number of words in sentences.

    while (sentencesToSkip <= additionalSentencesNumber) {
      wordsLeftLength += this.rawSentences[additionalSentencesNumber - sentencesToSkip].raw.split(' ').length;
      if (wordsLeftLength >= desiredPosition) {
        break;
      }
      sentencesToSkip++;
    }

    wordsLeftLength = wordsLeftLength - desiredPosition; //Number of words in the last utterance

    return new Promise((resolve) => {

      for(let i = 0; i <= sentencesToSkip; i++) {
        this._sentencesLeftFromUtteranceArray.unshift(new SpeechSynthesisUtterance(this.rawSentences[sentencesToSkip-i].raw))
      }

      wordsLeftLength = this._sentencesLeftFromUtteranceArray[0].text.split(' ').length - wordsLeftLength;
      const wordsLeft = this._sentencesLeftFromUtteranceArray[0].text.split(' ');

      //Add dummy sentence, as cancel method removes currently speaking sentence
      this._sentencesLeftFromUtteranceArray.unshift(new SpeechSynthesisUtterance());

      return this.cancel(true).then(() => {

        let symbolsCounter = this.calculateSymbolsToShift(wordsLeft, wordsLeftLength, 0, false);

        if(!symbolsCounter) {
          //Address an issue with WEPSpeech API cancel method.
            this.shift = 0;
            this.initializeText(true);
            this.sentenceIndex = 0;
            return resolve();

        } else {
          //Address an issue with WEPSpeech API cancel method.
            this.shift = symbolsCounter;
            return resolve();
        }


      });
    })
  }

  /**
   * Skip number of words passed in args
   * @param {number} desiredPosition (number of words to skip)
   * @returns {any}
   */
  performShift(desiredPosition: number) {

    let sentencesToSkip = 0;
    let wordsLeftLength = 0;
    const utteranceLengthDifference = this.rawSentences.length - this._sentencesLeftFromUtteranceArray.length;

    //Remove text that has already been ready from the current utterance.
    this._sentencesLeftFromUtteranceArray[0].text = this._sentencesLeftFromUtteranceArray[0].text.slice(this.currentSentenceCharPosition);

    //Skip sentences which do not have sufficient number of words
    while (sentencesToSkip < this._sentencesLeftFromUtteranceArray.length) {

      wordsLeftLength += this._sentencesLeftFromUtteranceArray[sentencesToSkip].text.split(' ').length;

      if (wordsLeftLength >= desiredPosition ) {
        wordsLeftLength = wordsLeftLength - desiredPosition; //Number of words in the last utterance
        wordsLeftLength = this._sentencesLeftFromUtteranceArray[sentencesToSkip].text.split(' ').length - wordsLeftLength;
        break;
      }
      sentencesToSkip++;
    }

    return new Promise((resolve) => {

      //Do nothing if there are no sufficient words to skip
      if (!this._sentencesLeftFromUtteranceArray[sentencesToSkip]) {
        return;
      }

      const wordsLeft = this._sentencesLeftFromUtteranceArray[sentencesToSkip].text.split(' ');

      return this.cancel(true).then(() => {
        let symbolsCounter = this.calculateSymbolsToShift(wordsLeft, wordsLeftLength-1, sentencesToSkip, true, utteranceLengthDifference);

        //Address an issue with WEBSpeech API cancel method.
          this.sentenceIndex += sentencesToSkip + utteranceLengthDifference;
          this.shift = symbolsCounter;
          return resolve();

      });
    })
  }

  speak(restart = false) {
    if(this.isForcedCancel && restart) {
      this.isForcedCancel = false;
    }
    this.initializeText(restart);
    clearInterval(this._utteranceReadingInterval);
    this.createUtteranceReadingInterval();
    this.applyCurrentUtteranceSettings();

    clearInterval(this._utteranceReadingInterval);

    this.speechEnded = true;
    this.charIndex = 0;

    if(!this._sentencesLeftFromUtteranceArray[0].voice.localService) {
      this.highlightSentence();
    }

    return new Promise((resolve) => {
      if(!this._sentencesLeftFromUtteranceArray[0]) {
        return resolve();
      }

      this._sentencesLeftFromUtteranceArray[0].onboundary = (event) => {
        this.updateBoundary(event.charIndex)
      };

      this._sentencesLeftFromUtteranceArray[0].onend = (endEvent: any) => {
        this.shift = 0;

        //Do not remove sentences from queue if text is invalid
        if (endEvent.srcElement && this._sentencesLeftFromUtteranceArray[0].text !== endEvent.srcElement.text) {
          return resolve(true);
        }
        this._sentencesLeftFromUtteranceArray.shift();
        this.sentenceIndex++;

        if (this._sentencesLeftFromUtteranceArray.length > 0) {
          return resolve(true);
        }
        this.sentenceIndex = 0;
        this.shift = 0;
        return resolve();
      }
      if (!this.isForcedCancel) {
        speechSynthesis.speak(this._sentencesLeftFromUtteranceArray[0]);
      } else {
        speechSynthesis.cancel();
        this.isForcedCancel = false;
        this.sentenceIndex = 0;
        this.shift = 0;
        return resolve();
      }

    })
  }


  setVoice(value: string): Promise<string> {
    return new Promise(async (resolve, reject) => {
      let synth = window.speechSynthesis;
      if (this.synthVoices.length === 0) {
        this.synthVoices = synth.getVoices();
      }
      let defaultVoice = head(filter(this.synthVoices, voice => voice.name === value));
      if (!defaultVoice) {
        return reject();
      }
      this.speechSynthesisUtterance.voice = defaultVoice;
      this.sentenceIndex = 0;
      this.shift = 0;
      return resolve(this.speechSynthesisUtterance.voice.name);
    });
  }

  setLang(value: string) {
    this.speechSynthesisUtterance.lang = value;
  }

  setRate(value: number) {
    this.speechSynthesisUtterance.rate = value;

    return Promise.resolve(value);
  }

  getRate() {
    return this.speechSynthesisUtterance.rate;
  }

  setVolume(value: number) {
    this.speechSynthesisUtterance.volume = value;
  }

  getVolume() {
    return this.speechSynthesisUtterance.volume;
  }

  setText(value: string) {

    let sentences = split(value);
    let sentencesFormatted: Array<SpeechSynthesisUtterance> = [];

    this.rawSentences = [];

    sentences.map((sentence) => {
      if (sentence.type === 'Sentence') {
        sentence.punctuationMarks = 0;
        this.rawSentences.push(sentence);
        return sentencesFormatted.push(new SpeechSynthesisUtterance(sentence.raw));
      } else {
        if(this.rawSentences.length > 0) {
          this.rawSentences[this.rawSentences.length-1].punctuationMarks++;
        }
      }
      return false;
    })

    this.initialText = value;
    this._sentencesLeftFromUtteranceArray = sentencesFormatted;
    this.speechSynthesisUtterance.text = value;
    this.sentenceIndex = 0;
    this.shift = 0;
  }

  setWordId(value: number) {
    this.wordIndex = value;
  }

  getWordId() {
    return this.wordIndex;
  }

  filterVoices(voices: Array<SpeechSynthesisVoice>): Array<IAudioVoice> {
    return mapVoices(voices);
  }

  getCurrentTextPart() {
    const words = this.getText().split(' ').filter(n => n);
    if (words.length < 0) return '';
    const textPart = words.slice(this.partWordIndex);
    this.partWordIndex = 0;
    return textPart.join(' ');
  }

  recalculateUtterances(sentenceIndex: number) {
    this.setText(this.initialText);
    this.isForcedCancel = false;
    this.sentenceIndex = sentenceIndex;
    for(let i = 0; i < this.sentenceIndex; i++) {
      this._sentencesLeftFromUtteranceArray.shift();
    }
    this._sentencesLeftFromUtteranceArray[0].text = this._sentencesLeftFromUtteranceArray[0].text.slice(this.currentSentenceCharPosition)
    this.shift = this.currentSentenceCharPosition;
  }


  protected updateBoundary(charIndex: number): void {

    let leftBoundaryPosition, rightBoundaryPosition: number;
    this.currentSentenceCharPosition = charIndex;

    charIndex = charIndex + this.shift;

    leftBoundaryPosition = rightBoundaryPosition = this.rawSentences[this.sentenceIndex].range[0] + charIndex;

    //Search for closest space
    while (!/[\s.!?,]/.test(this.initialText[rightBoundaryPosition]) && this.initialText.length > rightBoundaryPosition) {
      rightBoundaryPosition++;
    }


    while ((this.initialText[leftBoundaryPosition] !== ' ' && this.initialText[leftBoundaryPosition] !== '\n') && leftBoundaryPosition > 0) {
      leftBoundaryPosition--;
    }

    if(this.initialText[leftBoundaryPosition] === ' ' || this.initialText[this.leftBoundaryPosition] === '\n') {
      leftBoundaryPosition++;
    }

    this.leftBoundaryPosition = leftBoundaryPosition;
    this.rightBoundaryPosition = rightBoundaryPosition;

    this.storeInstance.dispatch(audioBoundaryReached({
      startPosition: leftBoundaryPosition,
      endPosition: rightBoundaryPosition,
    }));
  }

  protected applyCurrentUtteranceSettings(): void {
    if(this._sentencesLeftFromUtteranceArray.length > 0) {
      this._sentencesLeftFromUtteranceArray[0].voice = this.speechSynthesisUtterance.voice;
      this._sentencesLeftFromUtteranceArray[0].rate = this.speechSynthesisUtterance.rate;
    }
  }

  protected initializeText(restart: boolean): void {
    if (this.speechSynthesisUtterance.voice) {
      if (this._sentencesLeftFromUtteranceArray.length === 0 || restart) {
        if (this.initialText === '') {
          this.initialText = 'Type some text here';
        }

        this.setText(this.initialText)
      }
    }
  }
  private highlightSentence(): void {
    this.storeInstance.dispatch(audioBoundaryReached({
      startPosition: this.rawSentences[this.sentenceIndex].range[0],
      endPosition: this.rawSentences[this.sentenceIndex].range[1]
    }))
  }
}

export default WebSpeechAPI;
